/**
 * InputLogger - User Input & Text Editing (v2 Taxonomy)
 *
 * Taxonomy: input.{operation}[.{flavor}][.{sub-flavor}]
 *
 * Operations:
 * - keystroke: Raw keyboard input
 * - edit: Text editing operations
 * - caret: Cursor positioning and rendering
 * - selection: Text selection handling
 * - click: Mouse click handling
 * - nav: Navigation (Home/End/arrows)
 * - undo/redo: History operations
 * - ime: Input Method Editor
 * - paragraph: Paragraph boundary operations
 *
 * Additionally includes SharedInput operations under 'shared.*' taxonomy
 *
 * @example
 * InputLogger.edit.insert('hello', { position: 0 });
 * InputLogger.caret.move(42);
 * InputLogger.click.complete(); // Via ClickEventCollector
 */

import { WeBelieveLogger, captureCallerSource, captureFullStack } from '../WeBelieveLogger';
import { isLoggingEnabled } from './LoggingGuard';

// ============================================================================
// PERFORMANCE: Input logging is now controlled by LoggingConfigService
// Enable/disable from the Logs Dashboard without recompilation.
// Stack trace capture via captureCallerSource() is expensive (~200-300ms)
// and causes browser violations when called on every keystroke.
// ============================================================================

// Helper to check if input logging is enabled (supports inheritance)
const isInputEnabled = (subComponent?: string): boolean => {
  const component = subComponent ? `input.${subComponent}` : 'input';
  return isLoggingEnabled(component);
};

// No-op function for when logging is disabled
const noop = () => {};

// ============================================================================
// CONSOLIDATED CLICK EVENT SYSTEM
// Collects all click-related metrics into a single log entry
// ============================================================================

/**
 * Raw mouse event data - MANDATORY for click logging
 */
export interface ClickEventCoordinates {
  clientX: number;
  clientY: number;
  pageX: number;
  pageY: number;
  screenX: number;
  screenY: number;
  offsetX: number;
  offsetY: number;
  canvasCssX: number;
  canvasCssY: number;
  worldX: number;
  worldY: number;
  button: number;
  buttons: number;
  ctrlKey: boolean;
  shiftKey: boolean;
  altKey: boolean;
  metaKey: boolean;
}

/**
 * Complete click event data - one log per click
 */
export interface ClickEventData {
  timestamp: number;
  clickCount: number;
  coordinates: ClickEventCoordinates;
  pageIndex: number;
  canvasRect?: { left: number; top: number; width: number; height: number };
  graphemeIndex: number;
  validatedGraphemeIndex: number;
  graphemeValidationWarning?: string;
  charAtGrapheme?: string;
  charBefore?: string;
  charAfter?: string;
  textLength: number;
  isFromWasm: boolean;
  selectionAnchor: number;
  selectionFocus: number;
  selectionLength: number;
  caretX?: number;
  caretY?: number;
  caretHeight?: number;
  caretLineRange?: string;
  snapshotLinesCount: number;
  snapshotFirstLineRange?: string;
  snapshotLastLineRange?: string;
  caretMapLength: number;
  processingTimeMs?: number;
}

/**
 * Click Event Collector - gathers all metrics for a single click
 * Call start() on mousedown, add data progressively, then complete() to log
 */
class ClickEventCollectorClass {
  private currentClick: Partial<ClickEventData> | null = null;
  private startTime: number = 0;

  start(e: MouseEvent | React.MouseEvent, pageIndex: number, canvasRect: DOMRect): void {
    this.startTime = performance.now();
    const rect = canvasRect;

    this.currentClick = {
      timestamp: Date.now(),
      clickCount: 1,
      pageIndex,
      canvasRect: {
        left: rect.left,
        top: rect.top,
        width: rect.width,
        height: rect.height,
      },
      coordinates: {
        clientX: e.clientX,
        clientY: e.clientY,
        pageX: e.pageX,
        pageY: e.pageY,
        screenX: e.screenX,
        screenY: e.screenY,
        offsetX: (e as MouseEvent).offsetX ?? (e.clientX - rect.left),
        offsetY: (e as MouseEvent).offsetY ?? (e.clientY - rect.top),
        canvasCssX: e.clientX - rect.left,
        canvasCssY: e.clientY - rect.top,
        worldX: 0,
        worldY: 0,
        button: e.button,
        buttons: e.buttons,
        ctrlKey: e.ctrlKey,
        shiftKey: e.shiftKey,
        altKey: e.altKey,
        metaKey: e.metaKey,
      },
      graphemeIndex: 0,
      validatedGraphemeIndex: 0,
      textLength: 0,
      isFromWasm: false,
      selectionAnchor: 0,
      selectionFocus: 0,
      selectionLength: 0,
      snapshotLinesCount: 0,
      caretMapLength: 0,
    };
  }

  setWorldCoordinates(worldX: number, worldY: number): void {
    if (this.currentClick?.coordinates) {
      this.currentClick.coordinates.worldX = worldX;
      this.currentClick.coordinates.worldY = worldY;
    }
  }

  setClickCount(count: number): void {
    if (this.currentClick) {
      this.currentClick.clickCount = count;
    }
  }

  setGraphemeData(data: {
    graphemeIndex: number;
    validatedGraphemeIndex: number;
    warning?: string;
  }): void {
    if (this.currentClick) {
      this.currentClick.graphemeIndex = data.graphemeIndex;
      this.currentClick.validatedGraphemeIndex = data.validatedGraphemeIndex;
      this.currentClick.graphemeValidationWarning = data.warning;
    }
  }

  setCharacterDiagnostics(data: {
    charAtGrapheme: string;
    charBefore: string;
    charAfter: string;
    textLength: number;
    isFromWasm: boolean;
  }): void {
    if (this.currentClick) {
      this.currentClick.charAtGrapheme = data.charAtGrapheme;
      this.currentClick.charBefore = data.charBefore;
      this.currentClick.charAfter = data.charAfter;
      this.currentClick.textLength = data.textLength;
      this.currentClick.isFromWasm = data.isFromWasm;
    }
  }

  setSelectionResult(anchor: number, focus: number): void {
    if (this.currentClick) {
      this.currentClick.selectionAnchor = anchor;
      this.currentClick.selectionFocus = focus;
      this.currentClick.selectionLength = Math.abs(focus - anchor);
    }
  }

  setCaretResult(data: {
    x: number;
    y: number;
    height: number;
    lineRange?: string;
  }): void {
    if (this.currentClick) {
      this.currentClick.caretX = data.x;
      this.currentClick.caretY = data.y;
      this.currentClick.caretHeight = data.height;
      this.currentClick.caretLineRange = data.lineRange;
    }
  }

  setSnapshotInfo(data: {
    linesCount: number;
    firstLineRange?: string;
    lastLineRange?: string;
    caretMapLength: number;
  }): void {
    if (this.currentClick) {
      this.currentClick.snapshotLinesCount = data.linesCount;
      this.currentClick.snapshotFirstLineRange = data.firstLineRange;
      this.currentClick.snapshotLastLineRange = data.lastLineRange;
      this.currentClick.caretMapLength = data.caretMapLength;
    }
  }

  complete(): void {
    if (!this.currentClick) return;

    const processingTimeMs = performance.now() - this.startTime;
    this.currentClick.processingTimeMs = processingTimeMs;

    const data = this.currentClick as ClickEventData;
    // Use full stack capture for click events (they're important and not high-frequency)
    const source = captureFullStack(2);

    const clickType = data.clickCount === 3 ? 'triple' : data.clickCount === 2 ? 'double' : 'single';
    const message = `${clickType}-click at grapheme ${data.validatedGraphemeIndex} on page ${data.pageIndex}`;

    WeBelieveLogger.logWithSource('info', 'input.click.complete', message, source, {
      timestamp: data.timestamp,
      clickCount: data.clickCount,
      processingTimeMs: data.processingTimeMs?.toFixed(2),
      raw: {
        clientX: data.coordinates.clientX,
        clientY: data.coordinates.clientY,
        pageX: data.coordinates.pageX,
        pageY: data.coordinates.pageY,
        screenX: data.coordinates.screenX,
        screenY: data.coordinates.screenY,
        offsetX: data.coordinates.offsetX,
        offsetY: data.coordinates.offsetY,
        button: data.coordinates.button,
        buttons: data.coordinates.buttons,
      },
      canvas: {
        cssX: data.coordinates.canvasCssX.toFixed(1),
        cssY: data.coordinates.canvasCssY.toFixed(1),
        worldX: data.coordinates.worldX.toFixed(1),
        worldY: data.coordinates.worldY.toFixed(1),
      },
      modifiers: {
        ctrl: data.coordinates.ctrlKey,
        shift: data.coordinates.shiftKey,
        alt: data.coordinates.altKey,
        meta: data.coordinates.metaKey,
      },
      pageIndex: data.pageIndex,
      canvasRect: data.canvasRect,
      grapheme: {
        raw: data.graphemeIndex,
        validated: data.validatedGraphemeIndex,
        warning: data.graphemeValidationWarning,
      },
      character: {
        at: data.charAtGrapheme,
        before: data.charBefore,
        after: data.charAfter,
        textLength: data.textLength,
        isFromWasm: data.isFromWasm,
      },
      selection: {
        anchor: data.selectionAnchor,
        focus: data.selectionFocus,
        length: data.selectionLength,
      },
      caret: {
        x: data.caretX?.toFixed(1),
        y: data.caretY?.toFixed(1),
        height: data.caretHeight?.toFixed(1),
        lineRange: data.caretLineRange,
      },
      snapshot: {
        linesCount: data.snapshotLinesCount,
        firstLineRange: data.snapshotFirstLineRange,
        lastLineRange: data.snapshotLastLineRange,
        caretMapLength: data.caretMapLength,
      },
    });

    this.currentClick = null;
  }

  cancel(): void {
    this.currentClick = null;
  }

  isCollecting(): boolean {
    return this.currentClick !== null;
  }
}

export const ClickEventCollector = new ClickEventCollectorClass();

export const InputLogger = {
  // ============================================================================
  // Keystroke / Keyboard Input
  // ============================================================================
  keystroke: (key: string, data?: Record<string, unknown>) => {
    if (!isInputEnabled('keystroke')) return;
    const source = captureCallerSource();
    WeBelieveLogger.logWithSource('debug', 'input.keystroke', `Keystroke: ${key}`, source, { key, ...data });
  },

  keydown: (key: string, handled: boolean, data?: Record<string, unknown>) => {
    if (!isInputEnabled('keydown')) return;
    const source = captureCallerSource();
    WeBelieveLogger.logWithSource('debug', 'input.keydown', `Keydown: ${key} (${handled ? 'handled' : 'not handled'})`, source, { key, handled, ...data });
  },

  command: (command: string, data?: Record<string, unknown>) => {
    if (!isInputEnabled('command')) return;
    const source = captureCallerSource();
    WeBelieveLogger.logWithSource('info', 'input.command', `Command: ${command}`, source, { command, ...data });
  },

  // ============================================================================
  // Clipboard Operations
  // ============================================================================
  paste: {
    start: (length: number, mimeType: string, data?: Record<string, unknown>) => {
      if (!isInputEnabled('paste.start')) return;
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'input.paste.start', `Paste: ${length} chars (${mimeType})`, source, { length, mimeType, ...data });
    },

    complete: (length: number, data?: Record<string, unknown>) => {
      if (!isInputEnabled('paste.complete')) return;
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'input.paste.complete', `Paste completed: ${length} chars`, source, { length, ...data });
    },

    large: (length: number, data?: Record<string, unknown>) => {
      if (!isInputEnabled('paste.large')) return;
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('warn', 'input.paste.large', `Large paste detected: ${length} chars`, source, { length, ...data });
    },
  },

  cut: (length: number, data?: Record<string, unknown>) => {
    if (!isInputEnabled('cut')) return;
    const source = captureCallerSource();
    WeBelieveLogger.logWithSource('info', 'input.cut', `Cut: ${length} chars`, source, { length, ...data });
  },

  copy: (length: number, data?: Record<string, unknown>) => {
    if (!isInputEnabled('copy')) return;
    const source = captureCallerSource();
    WeBelieveLogger.logWithSource('debug', 'input.copy', `Copy: ${length} chars`, source, { length, ...data });
  },

  // ============================================================================
  // Text Editing Operations
  // ============================================================================
  edit: {
    insert: (text: string, data?: Record<string, unknown>) => {
      if (!isInputEnabled('edit.insert')) return;
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'input.edit.insert', `Insert: "${text.slice(0, 20)}${text.length > 20 ? '...' : ''}"`, source, { length: text.length, ...data });
    },

    delete: (count: number, direction: 'forward' | 'backward', data?: Record<string, unknown>) => {
      if (!isInputEnabled('edit.delete')) return;
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'input.edit.delete', `Delete ${count} chars (${direction})`, source, { count, direction, ...data });
    },

    deleteBoundary: (data?: Record<string, unknown>) => {
      if (!isInputEnabled('edit.delete.boundary')) return;
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('warn', 'input.edit.delete.boundary', 'Delete crosses paragraph boundary', source, data);
    },

    replace: (oldLength: number, newText: string, data?: Record<string, unknown>) => {
      if (!isInputEnabled('edit.replace')) return;
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'input.edit.replace', `Replace ${oldLength} chars with "${newText.slice(0, 20)}${newText.length > 20 ? '...' : ''}"`, source, { oldLength, newLength: newText.length, ...data });
    },

    newline: (data?: Record<string, unknown>) => {
      if (!isInputEnabled('edit.newline')) return;
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'input.edit.newline', 'Newline inserted', source, data);
    },

    newlineFormfeed: (data?: Record<string, unknown>) => {
      if (!isInputEnabled('edit.newline.formfeed')) return;
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'input.edit.newline.formfeed', 'Form-feed (page break) inserted', source, data);
    },

    flowCoalesce: (data?: Record<string, unknown>) => {
      if (!isInputEnabled('edit.flow.coalesce')) return;
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'input.edit.flow.coalesce', 'Edit coalesced into previous', source, data);
    },

    // Error and rollback logging for Option C
    rollback: (operation: string, data?: Record<string, unknown>) => {
      if (!isInputEnabled('edit.rollback')) return;
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('warn', 'input.edit.rollback', `Rolling back ${operation} to WASM state`, source, { operation, ...data });
    },

    graphemeFallback: (context: string, data?: Record<string, unknown>) => {
      if (!isInputEnabled('edit.graphemeFallback')) return;
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('warn', 'input.edit.grapheme-fallback', `${context}: Grapheme mapping not available - using UTF-16 fallback`, source, { context, ...data });
    },
  },

  // Top-level error logging for input operations
  editError: (operation: string, error: Error, data?: Record<string, unknown>) => {
    if (!isInputEnabled('error')) return;
    const source = captureCallerSource();
    WeBelieveLogger.logWithSource('error', 'input.error', `[${operation}] ${error.message}`, source, { operation, error: error.message, stack: error.stack, ...data });
  },

  commandBlocked: (command: string, reason: string, data?: Record<string, unknown>) => {
    if (!isInputEnabled('command.blocked')) return;
    const source = captureCallerSource();
    WeBelieveLogger.logWithSource('warn', 'input.command.blocked', `Command "${command}" blocked: ${reason}`, source, { command, reason, ...data });
  },

  // WASM data availability warnings
  wasmDataUnavailable: (context: string, data?: Record<string, unknown>) => {
    if (!isInputEnabled('wasm.unavailable')) return;
    const source = captureCallerSource();
    WeBelieveLogger.logWithSource('warn', 'input.wasm.unavailable', `[${context}] WASM data not available`, source, { context, ...data });
  },

  // Cursor position logging
  cursorCallbackMissing: (index: number, data?: Record<string, unknown>) => {
    if (!isInputEnabled('cursor.callback-missing')) return;
    const source = captureCallerSource();
    WeBelieveLogger.logWithSource('warn', 'input.cursor.callback-missing', `emitCursorChange: No callback registered for index ${index}`, source, { index, ...data });
  },

  cursorOutOfRange: (direction: 'ahead' | 'behind', position: number, boundary: number, data?: Record<string, unknown>) => {
    if (!isInputEnabled('cursor.out-of-range')) return;
    const source = captureCallerSource();
    WeBelieveLogger.logWithSource('warn', 'input.cursor.out-of-range', `Cursor at ${position} ${direction} of WASM data (${direction === 'ahead' ? 'max' : 'min'} ${boundary})`, source, { direction, position, boundary, ...data });
  },

  crossPageNavigation: (cursorPosition: number, currentLinesRange: string, allCachedLinesCount: number, data?: Record<string, unknown>) => {
    if (!isInputEnabled('cursor.cross-page')) return;
    const source = captureCallerSource();
    WeBelieveLogger.logWithSource('debug', 'input.cursor.cross-page', `Cross-page navigation fallback for cursor ${cursorPosition}`, source, { cursorPosition, currentLinesRange, allCachedLinesCount, ...data });
  },

  cursorFoundInCache: (position: number, lineIndex: number, rangeInfo: string, data?: Record<string, unknown>) => {
    if (!isInputEnabled('cursor.found-in-cache')) return;
    const source = captureCallerSource();
    WeBelieveLogger.logWithSource('debug', 'input.cursor.found-in-cache', `Found cursor ${position} in ALL cached lines (index ${lineIndex}, ${rangeInfo})`, source, { position, lineIndex, rangeInfo, ...data });
  },

  cursorNotFoundInCache: (position: number, lineRanges: string, data?: Record<string, unknown>) => {
    if (!isInputEnabled('cursor.not-found-in-cache')) return;
    const source = captureCallerSource();
    WeBelieveLogger.logWithSource('warn', 'input.cursor.not-found-in-cache', `Cursor ${position} not found in ALL cached lines: ${lineRanges}`, source, { position, lineRanges, ...data });
  },

  cursorInGap: (position: number, lineIndex: number, lineEnd: number, distance: number, data?: Record<string, unknown>) => {
    if (!isInputEnabled('cursor.in-gap')) return;
    const source = captureCallerSource();
    WeBelieveLogger.logWithSource('debug', 'input.cursor.in-gap', `Cursor ${position} in gap after line ${lineIndex} (end: ${lineEnd}, distance: ${distance})`, source, { position, lineIndex, lineEnd, distance, ...data });
  },

  pageBreakInsert: (position: number, data?: Record<string, unknown>) => {
    if (!isInputEnabled('pageBreak')) return;
    const source = captureCallerSource();
    WeBelieveLogger.logWithSource('info', 'input.page-break', `Page break inserted at position ${position}`, source, { position, ...data });
  },

  // ============================================================================
  // Caret/Cursor Operations
  // ============================================================================
  caret: {
    move: (position: number, data?: Record<string, unknown>) => {
      if (!isInputEnabled('caret.move')) return;
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'input.caret.move', `Caret moved to ${position}`, source, { position, ...data });
    },

    render: (x: number, y: number, height: number, data?: Record<string, unknown>) => {
      if (!isInputEnabled('caret.render')) return;
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'input.caret.render', `Caret rendered at (${x}, ${y})`, source, { x, y, height, ...data });
    },

    blink: (visible: boolean) => {
      if (!isInputEnabled('caret.blink')) return;
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'input.caret.blink', `Caret ${visible ? 'visible' : 'hidden'}`, source, { visible });
    },

    /**
     * Log caret dispose (cleanup)
     * Component: input.caret.dispose
     */
    dispose: (data?: Record<string, unknown>) => {
      if (!isInputEnabled('caret.dispose')) return;
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'input.caret.dispose', 'Caret disposed', source, data);
    },

    diag: {
      mapBuilt: (pageIndex: number, data: {
        pageStartGrapheme: number;
        pageEndGrapheme: number;
        lastLineIsEmpty: boolean;
        lastLineRange: string;
        extractedCaretEntries: number;
        caretIndices?: number[];
      }) => {
        if (!isInputEnabled('caret.diag.map-built')) return;
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('debug', 'input.caret.diag.map-built', `Page ${pageIndex} caret map built`, source, {
          pageIndex,
          ...data,
          caretIndices: data.caretIndices?.slice(0, 10),
        });
      },

      getPosition: (cursorGraphemeIndex: number, caretMapLen: number, caretIndices: string, lineRanges: string, snapshotId?: string, data?: Record<string, unknown>) => {
        if (ClickEventCollector.isCollecting()) return;
        if (!isInputEnabled('caret.diag.get-position')) return;
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('debug', 'input.caret.diag.get-position', `getCaretPosition(cursor=${cursorGraphemeIndex})`, source, {
          cursorGraphemeIndex,
          caretMapLen,
          caretIndices: caretIndices.length > 50 ? caretIndices.slice(0, 50) + '...' : caretIndices,
          lineRanges,
          snapshotId,
          ...data
        });
      },

      positionResult: (cursorGraphemeIndex: number, lineRange: string, caretEntryGrapheme: number | null, resultX: number, resultY: number, resultHeight: number, data?: Record<string, unknown>) => {
        if (ClickEventCollector.isCollecting()) return;
        if (!isInputEnabled('caret.diag.position-result')) return;
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('debug', 'input.caret.diag.position-result', `RESULT cursor=${cursorGraphemeIndex}`, source, {
          cursorGraphemeIndex,
          lineRange,
          caretEntryGrapheme,
          resultX: resultX.toFixed(2),
          resultY: resultY.toFixed(2),
          resultHeight: resultHeight.toFixed(2),
          ...data
        });
      },
    },

    gap: {
      positionMissing: (cursorPosition: number, nearestBefore: number | null, nearestAfter: number | null, gapContext: string, data?: Record<string, unknown>) => {
        if (!isInputEnabled('caret.gap.position-missing')) return;
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('warn', 'input.caret.gap.position-missing', `CURSOR IN GAP: pos=${cursorPosition}, nearest before=${nearestBefore}, after=${nearestAfter}`, source, {
          cursorPosition,
          nearestBefore,
          nearestAfter,
          gapContext,
          ...data
        });
      },

      detected: (afterPos: number, beforeNextPos: number, gapSize: number, lineContext: string, data?: Record<string, unknown>) => {
        if (!isInputEnabled('caret.gap.detected')) return;
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('warn', 'input.caret.gap.detected', `GAP: positions ${afterPos}→${beforeNextPos} (size=${gapSize}) ${lineContext}`, source, {
          afterPos,
          beforeNextPos,
          gapSize,
          lineContext,
          ...data
        });
      },

      analysis: (totalPositions: number, minPos: number, maxPos: number, missingPositions: number[], gaps: Array<{ start: number; end: number; size: number }>, data?: Record<string, unknown>) => {
        if (!isInputEnabled('caret.gap.analysis')) return;
        const source = captureCallerSource();
        const gapsStr = gaps.map(g => `[${g.start}→${g.end}]=${g.size}`).join(', ');
        WeBelieveLogger.logWithSource('info', 'input.caret.gap.analysis', `Caret map: ${totalPositions} entries, range [${minPos}-${maxPos}], ${missingPositions.length} missing`, source, {
          totalPositions,
          minPos,
          maxPos,
          missingCount: missingPositions.length,
          missingPositions: missingPositions.slice(0, 20),
          gaps: gapsStr,
          ...data
        });
      },
    },

    recovery: {
      snapToValid: (cursorGraphemeIndex: number, lineRange: string, gapDistance: number, usingNext: boolean, data?: Record<string, unknown>) => {
        if (ClickEventCollector.isCollecting()) return;
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('warn', 'input.caret.recovery.snap-to-valid', `GAP FIX: cursor=${cursorGraphemeIndex} using ${usingNext ? 'NEXT' : 'nearest'} line`, source, {
          cursorGraphemeIndex,
          lineRange,
          gapDistance,
          usingNext,
          ...data
        });
      },
    },

    lifecycle: {
      show: (wasActive: boolean, data?: Record<string, unknown>) => {
        if (ClickEventCollector.isCollecting()) return;
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('debug', 'input.caret.lifecycle.show', `show() called, isActive was: ${wasActive}`, source, { wasActive, ...data });
      },

      hide: (wasActive: boolean, data?: Record<string, unknown>) => {
        if (ClickEventCollector.isCollecting()) return;
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('debug', 'input.caret.lifecycle.hide', `hide() called, isActive was: ${wasActive}`, source, { wasActive, ...data });
      },

      dispose: (data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('info', 'input.caret.lifecycle.dispose', 'dispose() called - caret being destroyed', source, data);
      },
    },
  },

  // ============================================================================
  // Selection Operations
  // ============================================================================
  selection: {
    set: (start: number, end: number, data?: Record<string, unknown>) => {
      if (!isInputEnabled('selection.set')) return;
      if (ClickEventCollector.isCollecting()) return;
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'input.selection.set', `Selection: ${start}-${end}`, source, { start, end, length: end - start, ...data });
    },

    clear: (data?: Record<string, unknown>) => {
      if (!isInputEnabled('selection.clear')) return;
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'input.selection.clear', 'Selection cleared', source, data);
    },

    drag: {
      start: (anchor: number, data?: Record<string, unknown>) => {
        if (!isInputEnabled('selection.drag.start')) return;
        if (ClickEventCollector.isCollecting()) return;
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('debug', 'input.selection.drag.start', `Selection drag started at ${anchor}`, source, { anchor, ...data });
      },

      update: (current: number, data?: Record<string, unknown>) => {
        if (!isInputEnabled('selection.drag.update')) return;
        if (ClickEventCollector.isCollecting()) return;
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('debug', 'input.selection.drag.update', `Selection drag updated to ${current}`, source, { current, ...data });
      },

      complete: (start: number, end: number, data?: Record<string, unknown>) => {
        if (!isInputEnabled('selection.drag.complete')) return;
        if (ClickEventCollector.isCollecting()) return;
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('debug', 'input.selection.drag.complete', `Selection drag ended: ${start}-${end}`, source, { start, end, ...data });
      },
    },

    highlight: (ranges: Array<{start: number; end: number}>, data?: Record<string, unknown>) => {
      if (!isInputEnabled('selection.highlight')) return;
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'input.selection.highlight', `Highlight ${ranges.length} ranges`, source, { rangeCount: ranges.length, ...data });
    },
  },

  // ============================================================================
  // Click Operations (consolidated via ClickEventCollector)
  // ============================================================================
  click: {
    complete: () => {
      ClickEventCollector.complete();
    },

    diag: {
      positionWarning: (graphemeIndex: number, textLength: number, warning: string, data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('warn', 'input.click.diag.position-warning', warning, source, {
          graphemeIndex,
          textLength,
          ...data
        });
      },
    },
  },

  // ============================================================================
  // Navigation (Home/End/Arrows)
  // ============================================================================
  nav: {
    home: (fromPos: number, toPos: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'input.nav.home', `HOME key: ${fromPos} → ${toPos}`, source, {
        fromPos,
        toPos,
        moved: fromPos !== toPos,
        ...data
      });
    },

    end: (fromPos: number, toPos: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'input.nav.end', `END key: ${fromPos} → ${toPos}`, source, {
        fromPos,
        toPos,
        moved: fromPos !== toPos,
        ...data
      });
    },

    lineLookup: (position: number, foundLineIndex: number, lineRange: string, totalLines: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'input.nav.line-lookup', `Line lookup: pos ${position} → line ${foundLineIndex} ${lineRange}`, source, {
        position,
        foundLineIndex,
        lineRange,
        totalLines,
        ...data
      });
    },

    cachedLines: (linesCount: number, snapshotsCount: number, lineRanges: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'input.nav.cached-lines', `getCachedLines: ${linesCount} lines from ${snapshotsCount} snapshots`, source, {
        linesCount,
        snapshotsCount,
        lineRanges,
        ...data
      });
    },
  },

  // ============================================================================
  // Undo/Redo Operations
  // ============================================================================
  undo: {
    execute: (commandId: string, type: string, position: number, willDelete: number, willInsert: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'input.undo.execute', `UNDO: ${commandId}`, source, {
        commandId,
        type,
        position,
        willDelete,
        willInsert,
        ...data
      });
    },

    nothingToUndo: () => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'input.undo.nothing', 'Nothing to undo', source);
    },

    coalesce: (commandId: string, type: string, position: number, deletedLen: number, insertedLen: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'input.undo.coalesce', `Coalesced edit into command ${commandId}`, source, {
        commandId,
        type,
        position,
        deletedLen,
        insertedLen,
        ...data
      });
    },

    record: (commandId: string, type: string, position: number, deletedLen: number, insertedLen: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'input.undo.record', `Recorded command ${commandId}`, source, {
        commandId,
        type,
        position,
        deletedLen,
        insertedLen,
        ...data
      });
    },

    clear: (data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'input.undo.clear', 'Cleared undo history', source, data);
    },
  },

  redo: {
    execute: (commandId: string, type: string, position: number, willDelete: number, willInsert: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'input.redo.execute', `REDO: ${commandId}`, source, {
        commandId,
        type,
        position,
        willDelete,
        willInsert,
        ...data
      });
    },

    nothingToRedo: () => {
      if (!isInputEnabled('redo.nothing')) return;
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'input.redo.nothing', 'Nothing to redo', source);
    },
  },

  // ============================================================================
  // IME (Input Method Editor)
  // ============================================================================
  ime: {
    start: (data?: Record<string, unknown>) => {
      if (!isInputEnabled('ime.start')) return;
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'input.ime.start', 'IME composition started', source, data);
    },

    update: (text: string, data?: Record<string, unknown>) => {
      if (!isInputEnabled('ime.update')) return;
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'input.ime.update', `IME update: ${text}`, source, { text, ...data });
    },

    complete: (text: string, data?: Record<string, unknown>) => {
      if (!isInputEnabled('ime.complete')) return;
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'input.ime.complete', `IME composition ended: ${text}`, source, { text, ...data });
    },

    /** @deprecated Use InputLogger.ime.complete() */
    end: (text: string, data?: Record<string, unknown>) => {
      if (!isInputEnabled('ime.end')) return;
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'input.ime.complete', `IME composition ended: ${text}`, source, { text, ...data });
    },
  },

  // ============================================================================
  // Paragraph Boundary Operations
  // ============================================================================
  paragraph: {
    boundaryChange: (editType: 'insert' | 'delete', position: number, beforeParaCount: number, afterParaCount: number, affectedParaIds: string[], data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      const action = afterParaCount > beforeParaCount ? 'SPLIT' : afterParaCount < beforeParaCount ? 'MERGE' : 'SHIFT';
      WeBelieveLogger.logWithSource('info', 'input.paragraph.boundary-change', `${action}: ${editType} at ${position} changed ${beforeParaCount}→${afterParaCount} paragraphs`, source, {
        editType,
        position,
        action,
        beforeParaCount,
        afterParaCount,
        affectedParaIds: affectedParaIds.map(id => id.slice(0, 8)),
        ...data
      });
    },

    diag: {
      detected: (textLength: number, paragraphCount: number, paragraphs: Array<{ id: string; start: number; end: number; type?: string }>, data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        const paraRanges = paragraphs.map((p, i) => `P${i}:[${p.start}-${p.end})`).join(', ');
        WeBelieveLogger.logWithSource('debug', 'input.paragraph.diag.detected', `detectParagraphs: ${paragraphCount} paragraphs from ${textLength} graphemes`, source, {
          textLength,
          paragraphCount,
          paraRanges,
          paragraphs: paragraphs.map(p => ({ id: p.id.slice(0, 8), start: p.start, end: p.end, type: p.type })),
          ...data
        });
      },

      delimiterGap: (prevParaEnd: number, nextParaStart: number, gapSize: number, expectedGap: number, data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        const warning = gapSize !== expectedGap ? ' UNEXPECTED' : '';
        WeBelieveLogger.logWithSource(gapSize !== expectedGap ? 'warn' : 'debug', 'input.paragraph.diag.delimiter-gap', `Delimiter gap: [${prevParaEnd}→${nextParaStart}] = ${gapSize} (expected ${expectedGap})${warning}`, source, {
          prevParaEnd,
          nextParaStart,
          gapSize,
          expectedGap,
          isExpected: gapSize === expectedGap,
          ...data
        });
      },

      cumulativeOffset: (paragraphId: string, paragraphIndex: number, localEndIndex: number, cumulativeOffset: number, globalStart: number, globalEnd: number, data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('debug', 'input.paragraph.diag.cumulative-offset', `Para[${paragraphIndex}] offset calc: local=${localEndIndex}, cumulative=${cumulativeOffset} → global=[${globalStart}-${globalEnd})`, source, {
          paragraphId: paragraphId.slice(0, 8),
          paragraphIndex,
          localEndIndex,
          cumulativeOffset,
          globalStart,
          globalEnd,
          ...data
        });
      },
    },
  },

  // ============================================================================
  // Delete Key Diagnostics
  // ============================================================================
  deleteKey: {
    pressed: (cursorPosition: number, textLength: number, charBeingDeleted: string, charCode: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      const charDisplay = charBeingDeleted === '\n' ? '\\n' : charBeingDeleted === '\f' ? '\\f' : charBeingDeleted === '\r' ? '\\r' : charBeingDeleted;
      WeBelieveLogger.logWithSource('debug', 'input.delete-key.pressed', `DELETE pressed at pos=${cursorPosition}, deleting '${charDisplay}' (code=${charCode})`, source, {
        cursorPosition,
        textLength,
        charBeingDeleted: charDisplay,
        charCode,
        isNewline: charBeingDeleted === '\n',
        isPageBreak: charBeingDeleted === '\f',
        ...data
      });
    },

    result: (beforeCursor: number, afterCursor: number, beforeTextLen: number, afterTextLen: number, deletedChar: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      const charDisplay = deletedChar === '\n' ? '\\n' : deletedChar === '\f' ? '\\f' : deletedChar;
      WeBelieveLogger.logWithSource('debug', 'input.delete-key.result', `DELETE result: cursor ${beforeCursor}→${afterCursor}, text ${beforeTextLen}→${afterTextLen}, deleted '${charDisplay}'`, source, {
        beforeCursor,
        afterCursor,
        cursorMoved: beforeCursor !== afterCursor,
        beforeTextLen,
        afterTextLen,
        deletedChar: charDisplay,
        charsDeleted: beforeTextLen - afterTextLen,
        ...data
      });
    },

    noop: (reason: string, cursorPosition: number, textLength: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'input.delete-key.noop', `DELETE NOOP: ${reason}`, source, {
        reason,
        cursorPosition,
        textLength,
        ...data
      });
    },

    gap: (issue: string, cursorPosition: number, nearestCaretPos: number | null, lineRange: string | null, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('warn', 'input.delete-key.gap', `DELETE ISSUE: ${issue}`, source, {
        issue,
        cursorPosition,
        nearestCaretPos,
        cursorInGap: nearestCaretPos !== null && nearestCaretPos !== cursorPosition,
        lineRange,
        ...data
      });
    },
  },

  // ============================================================================
  // Line Range Analysis
  // ============================================================================
  lineRange: {
    allRanges: (snapshotId: string, lineCount: number, ranges: Array<{ index: number; start: number; end: number; isEmpty: boolean }>, totalGraphemes: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      const rangeStr = ranges.map(r => `L${r.index}:[${r.start}-${r.end})${r.isEmpty ? '' : ''}`).join(', ');
      WeBelieveLogger.logWithSource('debug', 'input.line-range.all-ranges', `Lines for ${snapshotId.slice(0, 8)}: ${lineCount} lines, ${totalGraphemes} graphemes`, source, {
        snapshotId: snapshotId.slice(0, 8),
        lineCount,
        totalGraphemes,
        lineRanges: rangeStr,
        ...data
      });
    },

    gap: (prevLineIndex: number, prevEnd: number, nextLineIndex: number, nextStart: number, gapSize: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      const warning = gapSize > 2 ? ' LARGE GAP' : gapSize === 0 ? '' : gapSize === 1 ? ' (single \\n)' : ' (\\n\\n delimiter)';
      WeBelieveLogger.logWithSource('debug', 'input.line-range.gap', `Line gap: L${prevLineIndex}→L${nextLineIndex} = [${prevEnd}→${nextStart}] size=${gapSize}${warning}`, source, {
        prevLineIndex,
        prevEnd,
        nextLineIndex,
        nextStart,
        gapSize,
        ...data
      });
    },

    cursorToLine: (cursorPosition: number, foundLineIndex: number | null, lineStart: number | null, lineEnd: number | null, isInGap: boolean, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      if (foundLineIndex !== null) {
        WeBelieveLogger.logWithSource('debug', 'input.line-range.cursor-to-line', `Cursor ${cursorPosition} → Line ${foundLineIndex} [${lineStart}-${lineEnd})`, source, {
          cursorPosition,
          foundLineIndex,
          lineStart,
          lineEnd,
          isInGap,
          ...data
        });
      } else {
        WeBelieveLogger.logWithSource('warn', 'input.line-range.cursor-to-line', `Cursor ${cursorPosition} NOT ON ANY LINE (in gap)`, source, {
          cursorPosition,
          isInGap: true,
          ...data
        });
      }
    },

    graphemeCoverage: (totalGraphemes: number, coveredByLines: number, coveredByCaretMap: number, orphanedPositions: number[], data?: Record<string, unknown>) => {
      if (!isInputEnabled('line-range.grapheme-coverage')) return;
      const source = captureCallerSource();
      const coverage = ((coveredByLines / totalGraphemes) * 100).toFixed(1);
      WeBelieveLogger.logWithSource('debug', 'input.line-range.grapheme-coverage', `Grapheme coverage: ${coveredByLines}/${totalGraphemes} (${coverage}%) by lines, ${orphanedPositions.length} orphaned`, source, {
        totalGraphemes,
        coveredByLines,
        coveredByCaretMap,
        coveragePercent: parseFloat(coverage),
        orphanedCount: orphanedPositions.length,
        orphanedPositions: orphanedPositions.slice(0, 20),
        ...data
      });
    },
  },

  // ============================================================================
  // Keyboard Listener Lifecycle
  // ============================================================================
  keyboard: {
    setup: (data?: Record<string, unknown>) => {
      if (!isInputEnabled('keyboard.setup')) return;
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'input.keyboard.setup', 'Keyboard listener setup', source, data);
    },

    teardown: (data?: Record<string, unknown>) => {
      if (!isInputEnabled('keyboard.teardown')) return;
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'input.keyboard.teardown', 'Keyboard listener teardown', source, data);
    },
  },

  // ============================================================================
  // Context/Provider Lifecycle
  // ============================================================================
  context: {
    init: (data?: Record<string, unknown>) => {
      if (!isInputEnabled('context.init')) return;
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'input.context.init', 'SharedInputContext initialized', source, data);
    },

    stateUpdate: (data?: Record<string, unknown>) => {
      if (!isInputEnabled('context.state-update')) return;
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'input.context.state-update', 'Input state updated', source, data);
    },

    layoutProvidersSet: (data?: Record<string, unknown>) => {
      if (!isInputEnabled('context.layout-providers-set')) return;
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'input.context.layout-providers-set', 'Layout providers set', source, data);
    },

    mounted: (data?: Record<string, unknown>) => {
      if (!isInputEnabled('context.mounted')) return;
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'input.context.mounted', 'SharedInputProvider MOUNTED', source, data);
    },

    unmounted: (data?: Record<string, unknown>) => {
      if (!isInputEnabled('context.unmounted')) return;
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'input.context.unmounted', 'SharedInputProvider UNMOUNTED', source, data);
    },

    wasmReady: (ready: boolean, data?: Record<string, unknown>) => {
      if (!isInputEnabled('context.wasm-ready')) return;
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'input.context.wasm-ready', `isWasmTextReady changed to: ${ready}`, source, { ready, ...data });
    },
  },

  // ============================================================================
  // Legacy Compatibility Methods
  // ============================================================================

  // Legacy editing object - maps to new edit.*
  editing: {
    insert: (text: string, data?: Record<string, unknown>) => {
      if (!isInputEnabled('edit.insert')) return;
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'input.edit.insert', `Insert: "${text.slice(0, 20)}${text.length > 20 ? '...' : ''}"`, source, { length: text.length, ...data });
    },
    delete: (count: number, direction: 'forward' | 'backward', data?: Record<string, unknown>) => {
      if (!isInputEnabled('edit.delete')) return;
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'input.edit.delete', `Delete ${count} chars (${direction})`, source, { count, direction, ...data });
    },
    replace: (oldLength: number, newText: string, data?: Record<string, unknown>) => {
      if (!isInputEnabled('edit.replace')) return;
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'input.edit.replace', `Replace ${oldLength} chars with "${newText.slice(0, 20)}${newText.length > 20 ? '...' : ''}"`, source, { oldLength, newLength: newText.length, ...data });
    },
    newline: (data?: Record<string, unknown>) => {
      if (!isInputEnabled('edit.newline')) return;
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'input.edit.newline', 'Newline inserted', source, data);
    },
  },

  // Legacy history object - maps to undo/redo
  history: {
    undo: (data?: Record<string, unknown>) => {
      if (!isInputEnabled('undo.execute')) return;
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'input.undo.execute', 'Undo', source, data);
    },
    redo: (data?: Record<string, unknown>) => {
      if (!isInputEnabled('redo.execute')) return;
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'input.redo.execute', 'Redo', source, data);
    },
    checkpoint: (data?: Record<string, unknown>) => {
      if (!isInputEnabled('undo.checkpoint')) return;
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'input.undo.checkpoint', 'History checkpoint created', source, data);
    },
    push: (operationType: string, data?: Record<string, unknown>) => {
      if (!isInputEnabled('undo.record')) return;
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'input.undo.record', `History push: ${operationType}`, source, data);
    },
  },

  // Legacy sharedInput - now under shared.* domain (see SharedLogger)
  sharedInput: {
    editReceived: (editId: number, type: string, position: number, textLen: number, textPreview: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'shared.edit.received', `UI_RECEIVED editId=${editId} type=${type} pos=${position} textLen=${textLen}`, source, {
        editId,
        type,
        position,
        textLen,
        textPreview: textPreview.slice(0, 20).replace(/\n/g, '\\n'),
        ...data
      });
    },

    handleEditCommand: (command: { type: string; position?: number; text?: string; count?: number }, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'shared.edit.applied', 'handleEditCommand called', source, { command, ...data });
    },

    deletePreState: (data: {
      textLength: number;
      deletePosition: number;
      deleteCount: number;
      charBeingDeleted?: string;
      charCodeBeingDeleted?: number;
      formFeedsBefore?: number;
      formFeedsAfter?: number;
      willDeleteFormFeed?: boolean;
    }) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'shared.edit.delete-pre-state', 'Pre-delete state', source, data);
    },

    textStateChanged: (oldLength: number, newLength: number, source_: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'shared.sync.text.complete', 'documentState.text changed', source, {
        source: source_,
        oldLength,
        newLength,
        ...data
      });
    },

    tsCommit: (editId: number, committedLen: number, committedTail: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'shared.edit.commit', `TS_COMMIT editId=${editId} committedLen=${committedLen}`, source, {
        editId,
        committedLen,
        committedTail: committedTail.slice(-20).replace(/\n/g, '\\n'),
        ...data
      });
    },

    wasmUpdatedPages: (editId: number, commandType: string, pageIndices: number[], pagesInfo: Array<{ pageIndex: number; hasLines: boolean; linesCount: number }>, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'shared.sync.pages.complete', `WASM updatedPages for editId=${editId}`, source, {
        editId,
        commandType,
        pageIndices,
        hasLinesOnEach: pagesInfo,
        ...data
      });
    },

    existingPagesBeforeMerge: (pagesInfo: Array<{ pageIndex: number; hasLines: boolean; linesCount: number }>, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'shared.sync.pages.merge', 'EXISTING pages BEFORE merge', source, {
        pages: pagesInfo,
        ...data
      });
    },

    mergedPagesAfterMerge: (editId: number, committedLen: number, totalPages: number, pagesInfo: Array<{ pageIndex: number; hasLines: boolean; linesCount: number; maxGrapheme?: number; hasStaleLines?: boolean }>, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'shared.sync.pages.merge', `MERGED pages AFTER merge (editId=${editId})`, source, {
        editId,
        committedLen,
        totalPages,
        pages: pagesInfo,
        ...data
      });
    },

    pageMerge: (existingCount: number, newCount: number, mergedCount: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'shared.sync.pages.merge', `Merged pages: existing=${existingCount}, new=${newCount}, merged=${mergedCount}`, source, {
        existingCount,
        newCount,
        mergedCount,
        ...data
      });
    },

    deleteRerender: (startPage: number, endPage: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'shared.edit.delete-rerender', `DELETE operation: Re-rendering pages ${startPage} to ${endPage}`, source, {
        startPage,
        endPage,
        ...data
      });
    },

    phase4IncrementalRender: (pageIndices: number[], data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'shared.sync.pages.incremental', `PHASE 4: Incremental render for pages`, source, {
        pageIndices,
        pageCount: pageIndices.length,
        ...data
      });
    },

    phase4Complete: (skippedRenderTrigger: boolean, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'shared.sync.complete', `PHASE 4: Called onDocumentTextChange for save sync${skippedRenderTrigger ? ', skipped setRenderTrigger (incremental render complete)' : ''}`, source, {
        skippedRenderTrigger,
        ...data
      });
    },

    enterDebug: (commandPosition: number, insertedText: string, expectedCaretIndex: number, paragraphSlices?: Array<{ idx: number; id: string; line_start?: number; line_end?: number }>, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'shared.edit.enter-debug', 'Post-Enter snapshot analysis', source, {
        commandPosition,
        insertedText: JSON.stringify(insertedText),
        insertedLen: insertedText.length,
        expectedCaretIndex,
        paragraphSlices,
        ...data
      });
    },

    canvasRegistered: (pageIndex: number, totalCount: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'shared.canvas.register', `Registered canvas renderer for page ${pageIndex} (total: ${totalCount})`, source, {
        pageIndex,
        totalCount,
        ...data
      });
    },

    canvasUnregistered: (pageIndex: number, totalCount: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'shared.canvas.unregister', `Unregistered canvas renderer for page ${pageIndex} (total: ${totalCount})`, source, {
        pageIndex,
        totalCount,
        ...data
      });
    },

    caretCallbackRegistered: (pageIndex: number, totalCount: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'shared.canvas.caret-register', `Registered caret callback for page ${pageIndex} (total: ${totalCount})`, source, {
        pageIndex,
        totalCount,
        ...data
      });
    },

    caretCallbackUnregistered: (pageIndex: number, totalCount: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'shared.canvas.caret-unregister', `Unregistered caret callback for page ${pageIndex} (total: ${totalCount})`, source, {
        pageIndex,
        totalCount,
        ...data
      });
    },

    incrementalPagesInitialized: (pageCount: number, totalPages: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'shared.sync.pages.init', `Initialized incrementalPagesRef with ${pageCount} pages (totalPages: ${totalPages})`, source, {
        pageCount,
        totalPages,
        ...data
      });
    },

    rendererDebug: (pageIndex: number, data: {
      hasPageData: boolean;
      pageDataIndex?: number;
      paragraphCount: number;
      changedLinesCount: number;
      changedLinesPreview: Array<{ graphemeRange: string; glyphCount: number }>;
    }) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'shared.render.debug', `RENDERER DEBUG page ${pageIndex}`, source, {
        pageIndex,
        ...data
      });
    },

    pageCountChanged: (previousCount: number, newCount: number, direction: 'increased' | 'decreased', data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'shared.sync.pages.count-change', `Page count ${direction}: ${previousCount} → ${newCount}`, source, {
        previousCount,
        newCount,
        direction,
        ...data
      });
    },

    fullRenderFallback: (reason: string, renderTrigger?: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'shared.render.fallback', `Triggering full render (${reason})`, source, {
        reason,
        renderTrigger,
        ...data
      });
    },

    wasmTextReady: (reason: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'shared.sync.wasm-ready', `WASM text ready: ${reason}`, source, {
        reason,
        ...data
      });
    },

    textSync: (data: { oldLength: number; newLength: number; source: string; preview?: string }) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'shared.sync.text', `Syncing text from ${data.source}`, source, data);
    },

    stalePageRemoved: (pageIndex: number, totalPages: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'shared.sync.pages.stale-removed', `Removed stale page ${pageIndex} (totalPages now ${totalPages})`, source, {
        pageIndex,
        totalPages,
        ...data
      });
    },

    affectedPagesFromWasm: (pageIndices: number[], data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'shared.sync.pages.affected', `Using WASM affectedPageIndices`, source, {
        pageIndices,
        ...data
      });
    },

    insertRenderSinglePage: (pageIndex: number, editPosition: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'shared.render.insert', `INSERT: Only rendering page ${pageIndex} for edit at position ${editPosition}`, source, {
        pageIndex,
        editPosition,
        ...data
      });
    },

    pageBreakInsert: (affectedPages: number[], data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'shared.render.page-break', `PAGE BREAK INSERT: Rendering all updated pages`, source, {
        affectedPages,
        ...data
      });
    },

    cursorBroadcast: (cursorIndex: number, callbackCount: number, useTargetedRouting: boolean, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'shared.caret.broadcast', `Broadcasting cursor change (${cursorIndex}) to ${callbackCount} pages`, source, {
        cursorIndex,
        callbackCount,
        useTargetedRouting,
        ...data
      });
    },

    keyboardSetup: (isReady: boolean, hasClient: boolean, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'shared.keyboard.setup', `Keyboard setup: isReady=${isReady} hasClient=${hasClient}`, source, {
        isReady,
        hasClient,
        ...data
      });
    },

    incrementalPagesBlocked: (reason: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'shared.sync.pages.blocked', `incrementalPagesRef update blocked: ${reason}`, source, {
        reason,
        ...data
      });
    },

    // ========================================================================
    // Formatting Update Flow (Line Spacing, etc.)
    // ========================================================================

    /**
     * Log formatting updater registered
     */
    formattingUpdaterRegistered: (data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'shared.formatting.updater-registered',
        'Formatting updater callback registered', source, data);
    },

    /**
     * Log formatting update triggered from toolbar
     */
    formattingUpdateTriggered: (data: {
      paragraphCount: number;
      paragraphIds: string[];
      lineSpacing?: number;
      fontSizePx?: number;
    }) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'shared.formatting.update-triggered',
        `Formatting update triggered for ${data.paragraphCount} paragraph(s)`,
        source, {
          paragraphCount: data.paragraphCount,
          paragraphIds: data.paragraphIds.map(id => id?.slice(0, 8)),
          lineSpacing: data.lineSpacing,
          fontSizePx: data.fontSizePx,
        });
    },

    /**
     * Log incrementalPagesRef update for formatting
     */
    formattingPagesUpdated: (data: {
      pagesCount: number;
      totalPages: number;
      previousPageCount?: number;
    }) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'shared.formatting.pages-updated',
        `incrementalPagesRef updated: ${data.pagesCount} pages (total: ${data.totalPages})`,
        source, data);
    },

    /**
     * Log canvas redraw triggered for formatting
     */
    formattingRedrawTriggered: (data: {
      registeredRenderers: number;
      pagesRedrawn: number;
    }) => {
      const source = captureCallerSource();
      const level = data.registeredRenderers === 0 ? 'warn' : 'info';
      WeBelieveLogger.logWithSource(level, 'shared.formatting.redraw-triggered',
        `Canvas redraw triggered: ${data.registeredRenderers} renderers registered`,
        source, data);
      if (data.registeredRenderers === 0) {
        WeBelieveLogger.logWithSource('warn', 'shared.formatting.no-renderers',
          'BREAKAGE: No canvas renderers registered - redraw will have no effect',
          source, data);
      }
    },

    /**
     * Log formatting handlers not ready
     */
    formattingHandlersNotReady: (data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('warn', 'shared.formatting.handlers-not-ready',
        'BREAKAGE: formattingUpdateHandlersRef is null - IncrementalPagesUpdaterRegistrar may not have mounted',
        source, data);
    },

    /**
     * Log formatting change propagated to parent (for save persistence)
     */
    formattingChangePropagated: (data: {
      paragraphCount: number;
      paragraphIds: string[];
    }) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'shared.formatting.change-propagated',
        `Paragraph formatting changes propagated to parent: ${data.paragraphCount} paragraphs`,
        source, data);
    },

    /**
     * Log formatting update skipped (no updates from WASM)
     */
    formattingNoUpdates: (data: {
      paragraphCount: number;
      updatedCount: number;
    }) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('warn', 'shared.formatting.no-updates',
        `WASM reported 0 updates for ${data.paragraphCount} paragraph(s) - formatting may not have changed`,
        source, data);
    },
  },

  // Legacy undoManager - maps to undo/redo
  undoManager: {
    coalesced: (commandId: string, type: string, position: number, deletedLen: number, insertedLen: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'input.undo.coalesce', `Coalesced edit into command ${commandId}`, source, {
        commandId,
        type,
        position,
        deletedLen,
        insertedLen,
        ...data
      });
    },

    recorded: (commandId: string, type: string, position: number, deletedLen: number, insertedLen: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'input.undo.record', `Recorded command ${commandId}`, source, {
        commandId,
        type,
        position,
        deletedLen,
        insertedLen,
        ...data
      });
    },

    undoExecuted: (commandId: string, type: string, position: number, willDelete: number, willInsert: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'input.undo.execute', `UNDO: ${commandId}`, source, {
        commandId,
        type,
        position,
        willDelete,
        willInsert,
        ...data
      });
    },

    redoExecuted: (commandId: string, type: string, position: number, willDelete: number, willInsert: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'input.redo.execute', `REDO: ${commandId}`, source, {
        commandId,
        type,
        position,
        willDelete,
        willInsert,
        ...data
      });
    },

    nothingToUndo: () => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'input.undo.nothing', 'Nothing to undo', source);
    },

    nothingToRedo: () => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'input.redo.nothing', 'Nothing to redo', source);
    },

    cleared: (data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'input.undo.clear', 'Cleared history', source, data);
    },
  },

  // Legacy caretDiag - maps to caret.diag/gap/recovery/lifecycle
  caretDiag: {
    getPosition: (cursorGraphemeIndex: number, caretMapLen: number, caretIndices: string, lineRanges: string, snapshotId?: string, data?: Record<string, unknown>) => {
      if (ClickEventCollector.isCollecting()) return;
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'input.caret.diag.get-position', `getCaretPosition(cursor=${cursorGraphemeIndex})`, source, {
        cursorGraphemeIndex,
        caretMapLen,
        caretIndices: caretIndices.length > 50 ? caretIndices.slice(0, 50) + '...' : caretIndices,
        lineRanges,
        snapshotId,
        ...data
      });
    },

    positionResult: (cursorGraphemeIndex: number, lineRange: string, caretEntryGrapheme: number | null, resultX: number, resultY: number, resultHeight: number, data?: Record<string, unknown>) => {
      if (ClickEventCollector.isCollecting()) return;
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'input.caret.diag.position-result', `RESULT cursor=${cursorGraphemeIndex}`, source, {
        cursorGraphemeIndex,
        lineRange,
        caretEntryGrapheme,
        resultX: resultX.toFixed(2),
        resultY: resultY.toFixed(2),
        resultHeight: resultHeight.toFixed(2),
        ...data
      });
    },

    gapFix: (cursorGraphemeIndex: number, lineRange: string, gapDistance: number, usingNext: boolean, data?: Record<string, unknown>) => {
      if (ClickEventCollector.isCollecting()) return;
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('warn', 'input.caret.recovery.snap-to-valid', `GAP FIX: cursor=${cursorGraphemeIndex} using ${usingNext ? 'NEXT' : 'nearest'} line`, source, {
        cursorGraphemeIndex,
        lineRange,
        gapDistance,
        usingNext,
        ...data
      });
    },

    show: (wasActive: boolean, data?: Record<string, unknown>) => {
      if (ClickEventCollector.isCollecting()) return;
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'input.caret.lifecycle.show', `show() called, isActive was: ${wasActive}`, source, { wasActive, ...data });
    },

    hide: (wasActive: boolean, data?: Record<string, unknown>) => {
      if (ClickEventCollector.isCollecting()) return;
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'input.caret.lifecycle.hide', `hide() called, isActive was: ${wasActive}`, source, { wasActive, ...data });
    },

    dispose: (data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'input.caret.lifecycle.dispose', 'dispose() called - caret being destroyed', source, data);
    },

    cleanupDispose: (runNumber: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'input.caret.lifecycle.cleanup', `CLEANUP - disposing caret (run #${runNumber})`, source, { runNumber, ...data });
    },

    useEffectRun: (runNumber: number, reason: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'input.caret.lifecycle.use-effect', `useEffect RUN #${runNumber} - ${reason}`, source, { runNumber, reason, ...data });
    },

    overlayInit: (data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'input.caret.lifecycle.overlay-init', 'Initializing caret overlay...', source, data);
    },

    instanceCreated: (hidden: boolean, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'input.caret.lifecycle.instance-created', `Caret instance created (${hidden ? 'hidden until positioned' : 'visible'})`, source, { hidden, ...data });
    },

    redrawPatched: (data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'input.caret.lifecycle.redraw-patched', 'Caret redraw method patched for smart rendering', source, data);
    },

    selectionRendererCreated: (data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'input.caret.lifecycle.selection-renderer', 'Selection renderer created', source, data);
    },

    mapBuilt: (pageIndex: number, data: {
      pageStartGrapheme: number;
      pageEndGrapheme: number;
      lastLineIsEmpty: boolean;
      lastLineRange: string;
      extractedCaretEntries: number;
      caretIndices?: number[];
    }) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'input.caret.diag.map-built', `Page ${pageIndex} caret map built`, source, {
        pageIndex,
        ...data,
        caretIndices: data.caretIndices?.slice(0, 10),
      });
    },
  },

  // Legacy clickDiag
  clickDiag: {
    characterAtClick: (info: {
      graphemeIndex: number;
      charAtGrapheme: string;
      charBefore: string;
      charAfter: string;
      pageIndex: number;
      worldPos: string;
      textLength: number;
      formFeedCount: number;
      isFromWasm: boolean;
      timestamp: number;
    }, context?: string, data?: Record<string, unknown>) => {
      if (ClickEventCollector.isCollecting()) return;
      const source = captureCallerSource();
      const label = context ? `Character at click position (${context})` : 'Character at click position';
      WeBelieveLogger.logWithSource('debug', 'input.click.diag.character', label, source, { ...info, ...data });
    },

    positionWarning: (graphemeIndex: number, textLength: number, warning: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('warn', 'input.click.diag.position-warning', warning, source, { graphemeIndex, textLength, ...data });
    },

    specialCharacter: (graphemeIndex: number, charType: string, warning?: string, data?: Record<string, unknown>) => {
      if (ClickEventCollector.isCollecting()) return;
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'input.click.diag.special-char', `Click on special character: ${charType}`, source, { graphemeIndex, charType, warning, ...data });
    },
  },

  // Legacy navigation
  navigation: {
    homeKey: (fromPos: number, toPos: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'input.nav.home', `HOME key: ${fromPos} → ${toPos}`, source, { fromPos, toPos, moved: fromPos !== toPos, ...data });
    },

    endKey: (fromPos: number, toPos: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'input.nav.end', `END key: ${fromPos} → ${toPos}`, source, { fromPos, toPos, moved: fromPos !== toPos, ...data });
    },

    lineLookup: (position: number, foundLineIndex: number, lineRange: string, totalLines: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'input.nav.line-lookup', `Line lookup: pos ${position} → line ${foundLineIndex} ${lineRange}`, source, { position, foundLineIndex, lineRange, totalLines, ...data });
    },

    cachedLines: (linesCount: number, snapshotsCount: number, lineRanges: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'input.nav.cached-lines', `getCachedLines: ${linesCount} lines from ${snapshotsCount} snapshots`, source, { linesCount, snapshotsCount, lineRanges, ...data });
    },
  },

  // Legacy paragraphBoundary
  paragraphBoundary: {
    detected: (textLength: number, paragraphCount: number, paragraphs: Array<{ id: string; start: number; end: number; type?: string }>, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      const paraRanges = paragraphs.map((p, i) => `P${i}:[${p.start}-${p.end})`).join(', ');
      WeBelieveLogger.logWithSource('debug', 'input.paragraph.diag.detected', `detectParagraphs: ${paragraphCount} paragraphs from ${textLength} graphemes`, source, {
        textLength,
        paragraphCount,
        paraRanges,
        paragraphs: paragraphs.map(p => ({ id: p.id.slice(0, 8), start: p.start, end: p.end, type: p.type })),
        ...data
      });
    },

    boundaryChanged: (editType: 'insert' | 'delete', position: number, beforeParaCount: number, afterParaCount: number, affectedParaIds: string[], data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      const action = afterParaCount > beforeParaCount ? 'SPLIT' : afterParaCount < beforeParaCount ? 'MERGE' : 'SHIFT';
      WeBelieveLogger.logWithSource('info', 'input.paragraph.boundary-change', `${action}: ${editType} at ${position} changed ${beforeParaCount}→${afterParaCount} paragraphs`, source, {
        editType,
        position,
        action,
        beforeParaCount,
        afterParaCount,
        affectedParaIds: affectedParaIds.map(id => id.slice(0, 8)),
        ...data
      });
    },

    cumulativeOffset: (paragraphId: string, paragraphIndex: number, localEndIndex: number, cumulativeOffset: number, globalStart: number, globalEnd: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'input.paragraph.diag.cumulative-offset', `Para[${paragraphIndex}] offset calc: local=${localEndIndex}, cumulative=${cumulativeOffset} → global=[${globalStart}-${globalEnd})`, source, {
        paragraphId: paragraphId.slice(0, 8),
        paragraphIndex,
        localEndIndex,
        cumulativeOffset,
        globalStart,
        globalEnd,
        ...data
      });
    },

    delimiterGap: (prevParaEnd: number, nextParaStart: number, gapSize: number, expectedGap: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      const warning = gapSize !== expectedGap ? ' UNEXPECTED' : '';
      WeBelieveLogger.logWithSource(gapSize !== expectedGap ? 'warn' : 'debug', 'input.paragraph.diag.delimiter-gap', `Delimiter gap: [${prevParaEnd}→${nextParaStart}] = ${gapSize} (expected ${expectedGap})${warning}`, source, {
        prevParaEnd,
        nextParaStart,
        gapSize,
        expectedGap,
        isExpected: gapSize === expectedGap,
        ...data
      });
    },
  },

  // Legacy deleteKey
  deleteKeyLegacy: {
    pressed: (cursorPosition: number, textLength: number, charBeingDeleted: string, charCode: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      const charDisplay = charBeingDeleted === '\n' ? '\\n' : charBeingDeleted === '\f' ? '\\f' : charBeingDeleted === '\r' ? '\\r' : charBeingDeleted;
      WeBelieveLogger.logWithSource('debug', 'input.delete-key.pressed', `DELETE pressed at pos=${cursorPosition}, deleting '${charDisplay}' (code=${charCode})`, source, {
        cursorPosition,
        textLength,
        charBeingDeleted: charDisplay,
        charCode,
        isNewline: charBeingDeleted === '\n',
        isPageBreak: charBeingDeleted === '\f',
        ...data
      });
    },

    result: (beforeCursor: number, afterCursor: number, beforeTextLen: number, afterTextLen: number, deletedChar: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      const charDisplay = deletedChar === '\n' ? '\\n' : deletedChar === '\f' ? '\\f' : deletedChar;
      WeBelieveLogger.logWithSource('debug', 'input.delete-key.result', `DELETE result: cursor ${beforeCursor}→${afterCursor}, text ${beforeTextLen}→${afterTextLen}, deleted '${charDisplay}'`, source, {
        beforeCursor,
        afterCursor,
        cursorMoved: beforeCursor !== afterCursor,
        beforeTextLen,
        afterTextLen,
        deletedChar: charDisplay,
        charsDeleted: beforeTextLen - afterTextLen,
        ...data
      });
    },

    noop: (reason: string, cursorPosition: number, textLength: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'input.delete-key.noop', `DELETE NOOP: ${reason}`, source, { reason, cursorPosition, textLength, ...data });
    },

    issue: (issue: string, cursorPosition: number, nearestCaretPos: number | null, lineRange: string | null, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('warn', 'input.delete-key.gap', `DELETE ISSUE: ${issue}`, source, {
        issue,
        cursorPosition,
        nearestCaretPos,
        cursorInGap: nearestCaretPos !== null && nearestCaretPos !== cursorPosition,
        lineRange,
        ...data
      });
    },
  },

  // Legacy caretMapGaps
  caretMapGaps: {
    analysis: (totalPositions: number, minPos: number, maxPos: number, missingPositions: number[], gaps: Array<{ start: number; end: number; size: number }>, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      const gapsStr = gaps.map(g => `[${g.start}→${g.end}]=${g.size}`).join(', ');
      WeBelieveLogger.logWithSource('info', 'input.caret.gap.analysis', `Caret map: ${totalPositions} entries, range [${minPos}-${maxPos}], ${missingPositions.length} missing`, source, {
        totalPositions,
        minPos,
        maxPos,
        missingCount: missingPositions.length,
        missingPositions: missingPositions.slice(0, 20),
        gaps: gapsStr,
        ...data
      });
    },

    cursorInGap: (cursorPosition: number, nearestBefore: number | null, nearestAfter: number | null, gapContext: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('warn', 'input.caret.gap.position-missing', `CURSOR IN GAP: pos=${cursorPosition}, nearest before=${nearestBefore}, after=${nearestAfter}`, source, {
        cursorPosition,
        nearestBefore,
        nearestAfter,
        gapContext,
        ...data
      });
    },

    gapFound: (afterPos: number, beforeNextPos: number, gapSize: number, lineContext: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('warn', 'input.caret.gap.detected', `GAP: positions ${afterPos}→${beforeNextPos} (size=${gapSize}) ${lineContext}`, source, {
        afterPos,
        beforeNextPos,
        gapSize,
        lineContext,
        ...data
      });
    },
  },

  // Legacy lineRangeAnalysis
  lineRangeAnalysis: {
    allRanges: (snapshotId: string, lineCount: number, ranges: Array<{ index: number; start: number; end: number; isEmpty: boolean }>, totalGraphemes: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      const rangeStr = ranges.map(r => `L${r.index}:[${r.start}-${r.end})${r.isEmpty ? '' : ''}`).join(', ');
      WeBelieveLogger.logWithSource('debug', 'input.line-range.all-ranges', `Lines for ${snapshotId.slice(0, 8)}: ${lineCount} lines, ${totalGraphemes} graphemes`, source, {
        snapshotId: snapshotId.slice(0, 8),
        lineCount,
        totalGraphemes,
        lineRanges: rangeStr,
        ...data
      });
    },

    lineGap: (prevLineIndex: number, prevEnd: number, nextLineIndex: number, nextStart: number, gapSize: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      const warning = gapSize > 2 ? ' LARGE GAP' : gapSize === 0 ? '' : gapSize === 1 ? ' (single \\n)' : ' (\\n\\n delimiter)';
      WeBelieveLogger.logWithSource('debug', 'input.line-range.gap', `Line gap: L${prevLineIndex}→L${nextLineIndex} = [${prevEnd}→${nextStart}] size=${gapSize}${warning}`, source, {
        prevLineIndex,
        prevEnd,
        nextLineIndex,
        nextStart,
        gapSize,
        ...data
      });
    },

    cursorToLine: (cursorPosition: number, foundLineIndex: number | null, lineStart: number | null, lineEnd: number | null, isInGap: boolean, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      if (foundLineIndex !== null) {
        WeBelieveLogger.logWithSource('debug', 'input.line-range.cursor-to-line', `Cursor ${cursorPosition} → Line ${foundLineIndex} [${lineStart}-${lineEnd})`, source, {
          cursorPosition,
          foundLineIndex,
          lineStart,
          lineEnd,
          isInGap,
          ...data
        });
      } else {
        WeBelieveLogger.logWithSource('warn', 'input.line-range.cursor-to-line', `Cursor ${cursorPosition} NOT ON ANY LINE (in gap)`, source, {
          cursorPosition,
          isInGap: true,
          ...data
        });
      }
    },

    graphemeCoverage: (totalGraphemes: number, coveredByLines: number, coveredByCaretMap: number, orphanedPositions: number[], data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      const coverage = ((coveredByLines / totalGraphemes) * 100).toFixed(1);
      WeBelieveLogger.logWithSource('debug', 'input.line-range.grapheme-coverage', `Grapheme coverage: ${coveredByLines}/${totalGraphemes} (${coverage}%) by lines, ${orphanedPositions.length} orphaned`, source, {
        totalGraphemes,
        coveredByLines,
        coveredByCaretMap,
        coveragePercent: parseFloat(coverage),
        orphanedCount: orphanedPositions.length,
        orphanedPositions: orphanedPositions.slice(0, 20),
        ...data
      });
    },
  },
};
